/**
 * Error tracking and reporting utilities
 * Logs errors in development, sends to service in production
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  timestamp: number;
  userAgent: string;
  url: string;
}

/**
 * Format error for display
 */
export const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
};

/**
 * Log error with context (dev only)
 */
export const logError = (
  error: unknown,
  context?: Partial<ErrorContext>
): void => {
  // Only log detailed errors in development
  if (!import.meta.env.DEV) {
    return;
  }

  const timestamp = Date.now();
  const fullContext: ErrorContext = {
    timestamp,
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context,
  };

  console.group(
    `%c❌ Error${context?.component ? ` in ${context.component}` : ''}`,
    'color: #ff6b6b; font-weight: bold; font-size: 12px;'
  );

  if (error instanceof Error) {
    console.error('Message:', error.message);
    console.error('Stack:', error.stack);
  } else {
    console.error('Error:', error);
  }

  console.table(fullContext);
  console.groupEnd();

  // Could send to error tracking service here
  // e.g., Sentry, LogRocket, etc.
};

/**
 * Create user-friendly error message
 */
export const getUserFriendlyMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check for common API errors
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      return 'You do not have permission to access this resource.';
    }
    if (error.message.includes('404') || error.message.includes('Not Found')) {
      return 'The requested resource was not found.';
    }
    if (error.message.includes('500') || error.message.includes('Internal Server')) {
      return 'A server error occurred. Please try again later.';
    }
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 'Network connection error. Please check your connection and try again.';
    }
  }

  return 'Something went wrong. Please try again.';
};

/**
 * Determine if error is recoverable
 */
export const isRecoverableError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    // Network errors are recoverable
    if (message.includes('network') || message.includes('timeout')) {
      return true;
    }
    // 5xx errors are recoverable
    if (message.includes('500') || message.includes('502') || message.includes('503')) {
      return true;
    }
    // Authentication errors - redirect, not recoverable locally
    if (message.includes('401') || message.includes('403')) {
      return false;
    }
  }
  return true;
};

/**
 * Error metadata for tracking
 */
export const captureErrorMetadata = (
  error: unknown,
  component?: string
): Record<string, unknown> => {
  return {
    isDev: import.meta.env.DEV,
    component,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
  };
};
